import React, { useMemo, useState } from 'react';
import { scaleLog, scaleTime } from 'd3';

const toDate = (ym) => new Date(Date.UTC(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1, 15));

/**
 * Log-rate vs time decline chart (history points + forecast lines + optional P-band).
 * Single history→forecast axis per the design handoff.
 */
export default function DeclineChart({
  history = [],
  series = [],
  band = null,
  height = 300,
  unit = 'Mcf/d',
  xLabel = null,
  monthsAxis = false,
  testId = 'decline-chart',
  historyLabel = null,
  sourceLabel = null,
}) {
  const [hover, setHover] = useState(null);
  const width = 760;
  const m = { t: 14, r: 16, b: 30, l: 56 };
  const { x, y, ticksY, ticksX, pts } = useMemo(() => {
    const all = [
      ...history.map(([d, q]) => ({ d, q })),
      ...series.flatMap((s) => s.points.map((p) => ({ d: p.date, q: p.rate }))),
      ...(band ? band.flatMap((b) => [{ d: b.date, q: b.hi }, { d: b.date, q: b.lo }]) : []),
    ].filter((p) => p.q > 0 && p.d);
    const ds = all.map((p) => toDate(p.d));
    const qs = all.map((p) => p.q);
    const x0 = ds.length ? new Date(Math.min(...ds)) : new Date();
    const x1 = ds.length ? new Date(Math.max(...ds)) : new Date();
    const qmax = qs.length ? Math.max(...qs) : 10;
    const qmin = Math.max(qs.length ? Math.min(...qs) : 1, qmax / 1e4, 0.1);
    const xs = scaleTime().domain([x0, x1]).range([m.l, width - m.r]);
    const ys = scaleLog().domain([qmin * 0.8, qmax * 1.25]).range([height - m.b, m.t]).clamp(true);
    return {
      x: xs,
      y: ys,
      ticksY: ys.ticks(5).filter((t) => String(t)[0] === '1' || ys.ticks(5).length < 6),
      ticksX: xs.ticks(6),
      pts: history.filter(([, q]) => q > 0),
    };
  }, [history, series, band, height]);

  const path = (points) =>
    points
      .filter((p) => p.rate > 0)
      .map((p, i) => `${i ? 'L' : 'M'}${x(toDate(p.date)).toFixed(1)},${y(p.rate).toFixed(1)}`)
      .join('');
  const bandPath = band?.length
    ? `${band.map((b, i) => `${i ? 'L' : 'M'}${x(toDate(b.date))},${y(b.hi)}`).join('')}${[...band]
        .reverse()
        .map((b) => `L${x(toDate(b.date))},${y(b.lo)}`)
        .join('')}Z`
    : null;

  return (
    <div className="aw-chart" data-testid={testId}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="Decline curve">
        {ticksY.map((t) => (
          <g key={`y${t}`}>
            <line x1={m.l} x2={width - m.r} y1={y(t)} y2={y(t)} stroke="#e2e8f0" />
            <text x={m.l - 6} y={y(t) + 4} textAnchor="end" className="aw-axis">
              {t >= 1000 ? `${t / 1000}k` : t}
            </text>
          </g>
        ))}
        {ticksX.map((t) => (
          <text key={`x${+t}`} x={x(t)} y={height - 10} textAnchor="middle" className="aw-axis">
            {monthsAxis ? `yr ${t.getUTCFullYear() - 2000}` : t.getUTCFullYear()}
          </text>
        ))}
        <text x={12} y={m.t + 4} className="aw-axis" transform={`rotate(-90 12 ${m.t + 4})`} textAnchor="end">
          {unit} (log)
        </text>
        {bandPath && <path d={bandPath} fill="rgba(232,163,23,0.16)" stroke="none" />}
        {pts.map(([d, q]) => (
          <circle
            key={d}
            cx={x(toDate(d))}
            cy={y(q)}
            r={2.2}
            fill="#475569"
            opacity={0.75}
            onMouseEnter={() => setHover({ d, q })}
            onMouseLeave={() => setHover(null)}
          />
        ))}
        {series.map((s) => (
          <path
            key={s.key}
            d={path(s.points)}
            fill="none"
            stroke={s.color}
            strokeWidth={s.width || 2}
            strokeDasharray={s.dash || undefined}
            data-series={s.key}
          />
        ))}
      </svg>
      <div className="aw-legend">
        <span>
          <i style={{ background: '#475569' }} /> {historyLabel || 'Actual'} ({history.length} mo)
          {sourceLabel ? ` · ${sourceLabel}` : ''}
        </span>
        {series.map((s) => (
          <span key={s.key}>
            <i style={{ background: s.color }} /> {s.label}
          </span>
        ))}
        {band && (
          <span>
            <i style={{ background: 'rgba(232,163,23,0.35)' }} /> {band.label || 'P10–P90'}
          </span>
        )}
        {hover && (
          <span className="aw-mono">
            {hover.d}: {hover.q.toFixed(1)} {unit}
          </span>
        )}
        {xLabel && <span className="aw-muted">{xLabel}</span>}
      </div>
    </div>
  );
}
